import { useEffect, useState } from "react";
import { X } from "lucide-react";
import client from "../lib/api";

/**
 * WelcomeToTheLoop.
 *
 * A one-time warm arc shown after the person's first goal read.
 * Turns the first cadence from a task into a rhythm. Tier drives
 * the tone; the raw reachable tier is NEVER shown as a label.
 * Same rule as trackers and classifier states.
 *
 * Rendered as a soft banner above the pledge card. On acknowledge,
 * we POST /welcome-loop/ack so it does not appear again.
 */
export function WelcomeToTheLoop({ pledgeId }) {
  const [ritual, setRitual] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!pledgeId) return () => { alive = false; };
    client.get(`/accountability/pledge/${pledgeId}/welcome-loop`)
      .then((r) => { if (alive) setRitual(r.data); })
      .catch(() => { if (alive) setRitual(null); });
    return () => { alive = false; };
  }, [pledgeId]);

  const ack = async () => {
    setDismissed(true);
    try { await client.post(`/accountability/pledge/${pledgeId}/welcome-loop/ack`); }
    catch (_e) { /* silent */ }
  };

  if (!ritual || dismissed || ritual.already_seen) return null;

  return (
    <div className="k-card mt-8" style={{ borderStyle: "dashed" }} data-testid="welcome-to-the-loop">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="k-label">welcome to your rhythm</div>
        <button onClick={ack} className="k-btn k-btn-ghost !p-2" data-testid="welcome-loop-dismiss">
          <X size={14} />
        </button>
      </div>
      <p className="klareet-serif" style={{ fontSize: "1.05rem", lineHeight: 1.5 }} data-testid="welcome-loop-arc">
        {ritual.warm_arc}
      </p>
      <p className="k-quiet !text-xs mt-2" data-testid="welcome-loop-cadence">
        {ritual.cadence}
      </p>
      {ritual.milestones_preview?.length > 0 && (
        <ol className="mt-4 space-y-2" data-testid="welcome-loop-preview">
          {ritual.milestones_preview.map((m) => (
            <li key={m.order} className="klareet-serif text-sm flex items-start gap-2">
              <div className="k-chip !text-xs !py-0 !px-2 mt-1" data-testid={`welcome-loop-tone-${m.order}`}>
                {m.tag_tone}
              </div>
              <div className="flex-1" data-testid={`welcome-loop-title-${m.order}`}>{m.title}</div>
            </li>
          ))}
        </ol>
      )}
      <div className="k-quiet !text-xs mt-4" data-testid="welcome-loop-trial-note">
        {ritual.trial_note}
      </div>
      <div className="flex justify-end mt-3">
        <button onClick={ack} className="k-btn k-btn-primary !text-xs" data-testid="welcome-loop-ack">
          got it, let&apos;s begin
        </button>
      </div>
    </div>
  );
}


/**
 * RefreshInvitation.
 *
 * Offered, never forced. When the /refresh-status endpoint reports
 * `is_due=true`, we render a small invitation card. The person can
 * accept (open a check-in session in a new tab) or decline. Decline
 * costs nothing.
 */
export function RefreshInvitation({ pledgeId, product }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!pledgeId) return () => { alive = false; };
    client.get(`/accountability/pledge/${pledgeId}/refresh-status`)
      .then((r) => { if (alive) setStatus(r.data); })
      .catch(() => { if (alive) setStatus(null); });
    return () => { alive = false; };
  }, [pledgeId]);

  const decline = async () => {
    if (!status?.day) return;
    setBusy(true);
    try {
      await client.post(
        `/accountability/pledge/${pledgeId}/refresh/decline`,
        { day: status.day },
      );
      setStatus({ ...status, is_due: false });
    } finally { setBusy(false); }
  };

  const accept = async () => {
    // Start a check-in mode session on the same product.
    setBusy(true);
    try {
      const { data } = await client.post("/demo/session/start", {
        product: product || "start", intake: {}, mode: "checkin",
      });
      // Redirect to the conversation surface. This preserves the
      // existing check-in flow already wired to /read/checkin.
      window.location.href = `/session/${data.session_id}`;
    } catch (_e) { setBusy(false); }
  };

  if (!status?.is_due) return null;

  return (
    <div className="k-warm-card mt-4 p-4" data-testid="refresh-invitation">
      <div className="k-label mb-2">a light check in</div>
      <p className="klareet-serif text-sm mb-1" data-testid="refresh-invitation-day">
        it has been about {status.day} days since you started. a short refresh, five to seven turns, would keep the read alive.
      </p>
      <p className="k-quiet !text-xs mb-3" data-testid="refresh-invitation-trial-note">
        {status.trial_note}
      </p>
      <div className="flex justify-end gap-2">
        <button
          onClick={decline}
          disabled={busy}
          className="k-btn k-btn-ghost !text-xs"
          data-testid="refresh-invitation-decline"
        >
          not now
        </button>
        <button
          onClick={accept}
          disabled={busy}
          className="k-btn k-btn-primary !text-xs"
          data-testid="refresh-invitation-accept"
        >
          {busy ? "starting" : "start the refresh"}
        </button>
      </div>
    </div>
  );
}
